import { Alert, Form, Button, message } from "antd";
import { useState, useEffect, useRef } from "react";
import * as monaco from 'monaco-editor';
import Editor from "@monaco-editor/react";

import FormItemUpload from "./components/FormItemUpload";
import ErrorMessageModal from "./components/ErrorMessageModal";
import type { StoryTimeDetailType } from "./content/storyTimeType"
import { header } from "../utils/zippyAdminHeader";
import ContentSection from "../../../components/layout/mainContent/ContentSection";
import MainContentFooter from "../../../components/layout/mainContent/MainContentFooter";
import { Colors } from "../../../constants/Colors";
import { apiCall } from "../../../utils/apiCall";

interface StoryTimeDetailScriptProps {
  storyGroupId: any;
  detailInfo: StoryTimeDetailType | undefined;
  scriptForm: any;
  userName: string | null | undefined;
}

const textStyle: React.CSSProperties = {
  color: Colors.notEditableText,
  lineHeight: "22px",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  margin: 0,
  padding: '4px 0',
};

const StoryTimeDetailScript = ({
  storyGroupId,
  detailInfo,
  scriptForm,
  userName,
}: StoryTimeDetailScriptProps) => {
  const form = scriptForm;
  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isMediaLoading, setIsMediaLoading] = useState<boolean>(false);
  const [waitingTimeout, setWaitingTimeout] = useState(false);
  const [showErrorMessageModal, setShowErrorMessageModal] = useState(false);
  const [showErrorMessageModalTitle, setShowErrorMessageModalTitle] = useState<string>("");
  const [showInfoText, setShowInfoText] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>("");
  const uploadQueueRef = useRef(Promise.resolve());

  const handleEditorDidMount = (editor: any) => {
    editorRef.current = editor;
    editorFormat();

    editor.onDidBlurEditorText(() => {
      editorFormat();
    });
  };

  const editorFormat = async () => {
    if (editorRef.current) {
      try {
        JSON.parse(editorRef.current.getValue());

        await editorRef.current
          .getAction("editor.action.formatDocument")
          ?.run();
      } catch (e) {
        return;
      }
    }
  }

  const handleMediaClick = async () => {
    setIsMediaLoading(true);
    setShowAlert(false);

    try {
      const script = form.getFieldValue('script');
      const payload = {
        script: JSON.stringify(JSON.parse(script)), //Compressing a Json String
      };

      const data = await apiCall({
        method: "POST",
        header: header,
        endPoint:
          process.env.REACT_APP_ZIPPY_ADMIN +
          `/stories/admin/groups/${storyGroupId}/mediaIntegration`,
        payload: payload
      });

      if (data && data.status) {
        const jsonString = JSON.stringify(data.result);
        form.setFieldValue('script', jsonString);

        if (editorRef.current) {
          editorRef.current.setValue(jsonString);
          await editorFormat();
        }

        setShowAlert(true);
      } else if (data.response.data.status === "MISSING_SOUND_FILE_IN_BLOB") {
        showMissingSonudFileError(data.response.data.errors);
      } else if (data.response.data.status === "MISSING_SOUND_FILE_NAME") {
        showMissingSoundFileNameError(data.response.data.errors)
      }
      else message.error(data.response.data.status);

    } catch (e) {
      console.log(e);
    }

    setIsMediaLoading(false);
  };

  const showMissingSonudFileError = (errors: string[]) => {
    let msg = `Please check if the file was uploaded.\n\n`;

    for (var j = 0; j < errors.length; j++) {
      msg += `- Missing file: ${errors[j]}\n`;
    }

    setErrorMsg(msg);
    setShowInfoText(false);
    setShowErrorMessageModal(true);
    setShowErrorMessageModalTitle("Missing or incorrect media file");
  }

  const showMissingSoundFileNameError = (errors: string[]) => {
    let msg = `Each "Block" refers to one block of content inside { ... }\n`;
    let blockIndex = 0;

    for (var i = 0; i < errors.length; i++) {
      const error = errors[i].split(".");
      const block = parseInt(error[1]) + 1;
      const path = error[2];
      const subBlock = parseInt(error[3]) + 1;

      if (blockIndex !== block) msg += `\nBlock ${block}:\n- MISSING_SOUND_FILE_NAME\n`;

      //steps.${blockIndex}.sentences.${subBlockIndex}
      if (path === "sentences") msg += `Sentences Sub Block ${subBlock}\n`;
      //steps.${blockIndex}.lines.${i}
      else if (path === "lines") msg += `Lines Sub Block ${subBlock}\n`;
      //steps.${blockIndex}.questions.${subBlockIndex}.question
      //steps.${blockIndex}.questions.${subBlockIndex}.feedback
      else if (path === "questions") msg += `Questions Sub Block ${subBlock} ${error[4]}\n`;

      blockIndex = block
    }

    setErrorMsg(msg);
    setShowInfoText(false);
    setShowErrorMessageModal(true);
    setShowErrorMessageModalTitle("Missing or unmatched sound file names.");
  }

  const handleSubmit = async (values: any) => {
    setIsLoading(true);
    try {
      const payload = {
        script: JSON.stringify(JSON.parse(values.script)), //Compressing a Json String
        operator: userName,
      };

      const data = await apiCall({
        method: "PATCH",
        header: header,
        endPoint:
          process.env.REACT_APP_ZIPPY_ADMIN +
          `/stories/admin/groups/${storyGroupId}/script`,
        payload: payload
      });

      if (data && data.status) message.success(data.status);
      else if (data.response.data.status === "INVALID_SCRIPT_CONTENT") {
        let msg = `Each "Block" refers to one block of content inside { ... }\n`;
        let showInfo = true;

        for (var i = 0; i < data.response.data.errors.length; i++) {
          msg += `\nBlock ${data.response.data.errors[i].index + 1}:\n`;

          const errors = data.response.data.errors[i].errors;
          for (var j = 0; j < errors.length; j++) {
            msg += `- ${errors[j]}\n`;
            if (errors[j].includes("INVALID_WORD_SEGMENTS")) showInfo = false;
          }
        }

        setErrorMsg(msg);
        setShowInfoText(showInfo);
        setShowErrorMessageModal(true);
        setShowErrorMessageModalTitle("Invalid or unmatched script content.");
      } else if (data.response.data.status === "MISSING_SOUND_FILE_IN_BLOB") {
        showMissingSonudFileError(data.response.data.errors);
      } else if (data.response.data.status === "MISSING_SOUND_FILE_NAME") {
        showMissingSoundFileNameError(data.response.data.errors)
      }
      else message.error(data.response.data.status);

    } catch (e) {
      console.log(e);
    }

    setIsLoading(false);
  };

  useEffect(() => {
    if (detailInfo) {
      const animationFileList = detailInfo.media.animation
        ? [{
          uid: detailInfo.media.animation.mediaId.toString(),
          name: detailInfo.media.animation.fileName,
          status: "done",
          url: detailInfo.media.animation.url,
        }]
        : [];

      const audiosFileList = Array.isArray(detailInfo.media.audios)
        ? detailInfo.media.audios.map(audio => ({
          uid: audio.mediaId.toString(),
          name: audio.fileName,
          status: "done",
          url: audio.url,
        }))
        : [];

      const imagesFileList = Array.isArray(detailInfo.media.images)
        ? detailInfo.media.images.map(image => ({
          uid: image.mediaId.toString(),
          name: image.fileName,
          status: "done",
          url: image.url,
        }))
        : [];

      form.setFieldsValue({
        animation: animationFileList,
        audios: audiosFileList,
        images: imagesFileList,
        script: detailInfo.script ? detailInfo.script : "",
      });
    }
  }, [detailInfo, form]);

  return (
    <ContentSection title="" showTitle={false}>
      <Form
        form={form}
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 14 }}
        onFinish={handleSubmit}
      >
        <Form.Item label="Story title">
          <p style={textStyle}>{detailInfo?.title}</p>
        </Form.Item>
        <Form.Item label="CAP">
          <p style={textStyle}>{Array.isArray(detailInfo?.caps) ? detailInfo?.caps.join(', ') : ''}</p>
        </Form.Item>
        <Form.Item
          label="Upload Media"
          required={true}
        >
          <span style={{ color: "rgba(0,0,0,0.45)" }}>
            Once uploaded, files are automatically stored and ready for integration with script content.
          </span>
          <br />
          <div className="infoBoxes">
            <div className="first-sub-box">
              <p className="title">Animation file</p>
              <FormItemUpload
                storyGroupId={storyGroupId}
                userName={userName}
                waitingTimeout={waitingTimeout}
                setWaitingTimeout={setWaitingTimeout}
                uploadQueueRef={uploadQueueRef}
                name="animation"
                btnText="Upload (Max: 1)"
                type="mp4"
                form={form}
              />
              <span style={{ color: "rgba(0,0,0,0.45)" }}>File format: .mp4.</span>
            </div>
            <div className="sub-box">
              <p className="title">Audio files</p>
              <FormItemUpload
                storyGroupId={storyGroupId}
                userName={userName}
                waitingTimeout={waitingTimeout}
                setWaitingTimeout={setWaitingTimeout}
                uploadQueueRef={uploadQueueRef}
                name="audios"
                btnText="Upload Directory"
                type="mp3"
                form={form}
              />
            </div>
            <div className="sub-box">
              <p className="title">Image files</p>
              <FormItemUpload
                storyGroupId={storyGroupId}
                userName={userName}
                waitingTimeout={waitingTimeout}
                setWaitingTimeout={setWaitingTimeout}
                uploadQueueRef={uploadQueueRef}
                name="images"
                btnText="Upload Directory"
                type="png"
                form={form}
              />
            </div>
          </div>
        </Form.Item>
        <Form.Item
          label="Script editor"
          required={true}
        >
          <div className="json-editor-div">
            <Form.Item
              name="script"
              rules={[
                {
                  required: true,
                  validator: (_, value) => {
                    if (!value || !value.trim()) {
                      return Promise.reject("This field is required!");
                    }

                    try {
                      JSON.parse(value);
                    } catch (e) {
                      return Promise.reject("Invalid JSON format!");
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Editor
                height="500px"
                defaultLanguage="json"
                onMount={handleEditorDidMount}
                options={{
                  minimap: { enabled: false },
                  formatOnType: true,
                  formatOnPaste: true,
                  automaticLayout: true,
                }}
              />
            </Form.Item>
          </div>
          {showAlert && (
            <Alert
              message="Word segmentation is complete. The script now matches the audio timing."
              type="success"
              style={{ marginBottom: 20 }}
              showIcon
            />)}
          <Button
            loading={isMediaLoading}
            type="primary"
            style={{ padding: "4px 15px" }}
            onClick={handleMediaClick}>
            Media integration
          </Button>
          <p style={{ padding: "10px 0px", lineHeight: "22px" }}>
            <span style={{ fontWeight: "bold" }}>About Media Integration:</span><br />
            Pressing the Media Integration button will parse your audio into JSON, including word segments and timing information. Each use is billed based on the number of times you press the button or the length of the audio. To reduce costs, try to minimize the number of times you trigger Media Integration.
            This action will update the script. Please back up your edits if needed.
          </p>
        </Form.Item>
        <MainContentFooter
          isLoading={isLoading}
          backTo="/zippy-and-me/story-time"
          btnText="Save"
        />
      </Form>
      <ErrorMessageModal
        open={showErrorMessageModal}
        title={showErrorMessageModalTitle}
        text={errorMsg}
        handleDone={() => {
          setErrorMsg("");
          setShowInfoText(false);
          setShowErrorMessageModal(false);
          setShowErrorMessageModalTitle("");
        }}
        showInfoText={showInfoText}
        infoText="No Media Integration needed after error fixes."
      />
    </ContentSection >
  );
};

export default StoryTimeDetailScript;
